// packages/app/src/app/shared/transaction/all-transaction/transaction-history-table/payment-columns.tsx
"use client";

import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import DateCell from "@core/ui/date-cell";
import { Text } from "rizzui";
import { getStatusBadge } from "@core/components/table-utils/get-status-badge";
import TableRowActionGroup from "../../../table-row-action-group";
import type { Payments } from "packages/app/src/types";
import TableRowActionGroupWithDelete from "../../../table-row-action-with-delete";

function formatCurrency(
  amount: number | null | undefined,
  code: string | null | undefined
): string {
  if (amount == null) return "-";
  if (!code) {
    return amount.toLocaleString(undefined, { minimumFractionDigits: 2 });
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
    })} ${code}`;
  }
}

const columnHelper = createColumnHelper<Payments>();

export const paymentColumns = [
  columnHelper.display({
    id: "actions",
    header: "Actions",
    size: 80,
    cell: ({ row, table: { options } }) => (
      <TableRowActionGroupWithDelete
        onDelete={() =>
          options.meta?.handleDeleteRow?.(row.original as Payments)
        }
      />
    ),
  }),

  columnHelper.accessor("_id", {
    header: "Payment ID",
    size: 200,
    cell: ({ row }) => <Text className="font-medium">{row.original._id}</Text>,
  }),

  columnHelper.accessor("campaignId", {
    header: "Campaign ID",
    size: 180,
    cell: ({ row }) => <Text>{row.original.campaignId ?? "-"}</Text>,
  }),

  columnHelper.accessor("donor", {
    header: "Donor",
    size: 220,
    cell: ({ row }) => (
      <Text className="truncate">{row.original.donor ?? "-"}</Text>
    ),
  }),

  columnHelper.accessor("amount", {
    header: "Amount Received",
    size: 140,
    cell: ({ row }) => (
      <Text className="font-semibold whitespace-nowrap">
        {formatCurrency(row.original.amount, row.original.currencyType)}
      </Text>
    ),
  }),
  columnHelper.accessor("totalAmount", {
    header: "Amount Donated",
    size: 140,
    cell: ({ row }) => (
      <Text className="font-semibold whitespace-nowrap">
        {formatCurrency(row.original.totalAmount, row.original.currencyType)}
      </Text>
    ),
  }),

  columnHelper.accessor("tip", {
    header: "Tip",
    size: 120,
    cell: ({ row }) => (
      <Text className="whitespace-nowrap">
        {formatCurrency(row.original.tip, row.original.currencyType)}
      </Text>
    ),
  }),

  // columnHelper.accessor("totalAmount", {
  //   header: "Total",
  //   size: 160,
  //   cell: ({ row }) => (
  //     <Text className="font-semibold whitespace-nowrap">
  //       {formatCurrency(row.original.totalAmount, row.original.currencyType)}
  //     </Text>
  //   ),
  // }),

  columnHelper.accessor("currencyType", {
    header: "Currency",
    size: 100,
    cell: ({ row }) => <Text>{row.original.currencyType ?? "-"}</Text>,
  }),

  columnHelper.accessor("status", {
    header: "Status",
    size: 120,
    cell: ({ row }) => {
      const s = row.original.status;
      return s ? getStatusBadge(s) : <Text>-</Text>;
    },
  }),

  columnHelper.accessor("createdAt", {
    header: "Created",
    size: 180,
    cell: ({ row }) => {
      const date = row.original.createdAt
        ? new Date(row.original.createdAt)
        : null;
      return date ? <DateCell date={date} /> : <Text>-</Text>;
    },
  }),
];
